import { defineAsyncComponent, reactive, toRaw } from "vue";
import i18n from "../../../i18n";
import store from "../../../store";
import { createLoading } from "../../../utils/loading";
import { Col, Row } from "ant-design-vue";
import { has } from "../../../utils/menu-context";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../utils/button";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../utils/page";
import { required, validateError } from "../../../utils/validate";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../app.logic";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickDeleteAllClient,
    onClickEdit,
    onClickView
} from "../../../utils/form";
import { preview, download, niceBytes } from "../../../utils/file";
import { createColumnInput } from "../../../components/table/c-table.logic";
import {
    createFieldAttachment,
    createFieldInput
} from "../../../components/input/template-builder/template-builder.logic";
import { modalStyleForm, modalStyleIFrame } from "../../../utils/constant";
import lodash from "lodash";

const CIFrame = defineAsyncComponent(() => import('../../../components/iframe/CIFrame'))
const AttachmentForm = defineAsyncComponent(() => import('./AttachmentForm'))

export const { isLoading, setIsLoading } = createLoading()

export const actionPrefix = 'resource/v1/master/attachment'
export const routerPrefix = 'attachments'

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form })
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria })
export const callSearchByModelAndRefId = ({ criteria }) => store.dispatch(`${actionPrefix}/searchByModelAndRefId`, { criteria })
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id })
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id })
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } })
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids })
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids })
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } })
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria })

export const onSearch = ({ page }) => {
    const sort = page.sort || []
    setIsLoading(true)
    window.onSearch?.({ page })
    return callSearchByModelAndRefId({
        criteria: {
            ...page,
            data: [],
            sort: [...sort]
        }
    })
        .then(page => state.page = { ...page, sort })
        .finally(() => setIsLoading(false))
}

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true)
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then((record) => onClickView({ record, routerPrefix }))
        })
        .catch((error) => validateError({ error }));
}

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch((error) => {
            validateError({ error })
            throw error
        });
}

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ['**'],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
        }
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
            children: [{ name: 'children', select: ['name'], page: 0, size: 1000000, isRecursive: true }],
        }
    }
}

export const createColumns = () => {
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.attachment.documentName"), dataIndex: "documentName" }),
        createColumnInput({ title: () => i18n.global.t("model.attachment.name"), dataIndex: "name" }),
        createColumnInput({
            title: () => i18n.global.t("model.attachment.size"),
            dataIndex: "size",
            customRender: ({ record }) => <div>{niceBytes(record.size)}</div>,
            align: 'center',
            search: {
                visible: () => false
            }
        }),
        {
            visible: () => true,
            title: () => i18n.global.t("model.attachment.modelName"),
            dataIndex: "url",
            sorter: false,
            width: 150,
            align: 'center',
            customRender: ({ record }) => {
                if (record?.type === "application/pdf") {
                    return <div>
                        <a href="javascript:void(0)" className="mx-2" onClick={() => preview({ file: record })}>{i18n.global.t("button.preview")}</a>
                        <a href="javascript:void(0)" className="mx-2" onClick={() => download({ file: record })}>{i18n.global.t("button.download")}</a>
                    </div>
                } else {
                    return <div>
                        <a href="javascript:void(0)" className="mx-2" onClick={() => download({ file: record })}>{i18n.global.t("button.download")}</a>
                    </div>
                }
            }
        }
    ]
    return columns
}

export const createForm = () => {
    return {
        model: null,
        group: null,
        refId: null,
        url: null,
        blob: null,
        type: null,
        name: null,
        documentName: null,
        size: null,
        path: null,
    }
}

export const createRules = () => {
    return {
        name: [required],
        documentName: [required],
    }
}

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly
    const useForm = data.useForm
    const modelRef = useForm.modelRef
    return [
        createFieldInput({
            key: 'documentName',
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.attachment.documentName'),
            inputProp: () => ({
                value: modelRef.documentName,
                'onUpdate:value': value => modelRef.documentName = value,
                disabled: viewOnly
            })
        }),
        createFieldInput({
            key: 'name',
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.attachment.name'),
            inputProp: () => ({ value: modelRef.name, disabled: true })
        }),
        createFieldAttachment({
            key: 'refAttach',
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.attachment.modelName'),
            inputProp: () => ({
                value: modelRef.id || modelRef.blob ? modelRef : null,
                'onUpdate:value': value => {
                    modelRef.name = value?.name;
                    modelRef.documentName = modelRef.documentName ? modelRef.documentName : value?.name;
                    modelRef.blob = value?.blob;
                    modelRef.isSave = value?.isSave;
                    modelRef.size = value?.size;
                    modelRef.type = value?.type;
                    modelRef.group = value?.group;
                },
                disabled: viewOnly,
                group: props.group,
            })
        }),
        createFieldInput({
            key: 'size',
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.attachment.size'),
            inputProp: () => ({
                value: modelRef.size ? niceBytes(modelRef.size) : modelRef.size,
                disabled: true
            })
        }),
    ]
}

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.name : i18n.global.t('button.create')
}

export const loadForm = ({ props, data }) => {
    setIsLoading(true)
    const { form } = props
    const { id, useForm } = data
    const { modelRef } = useForm
    let promise = null
    if (form) {
        promise = Promise.resolve({ data: [form] })
    } else if (id) {
        promise = callSearchByModelAndRefId({ criteria: createSearchOneCriteria({ id }) })
    } else {
        promise = Promise.resolve({ data: [modelRef] })
    }
    return promise
        .then(page => initUseForm({ data, page, routerPrefix }))
        .finally(() => setIsLoading(false))
}

export const initState = ({ props }) => {
    const menuContext = props.menuContext
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch, setIsLoading }),
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) => onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch, setIsLoading }),
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) => onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch, setIsLoading }),
        }),
        createManageExportCsv({ menuContext }),
    ]
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })]
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) }),
    ]
}

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs })
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs })
        .then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                        default: () => createAppPageHeaderDefault({ props, data, refs })
                    }
                });
            }
        })
}

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = []
    const col2 = []
    const { menuContext, viewOnly } = props
    const { id, isActive } = data.useForm.modelRef
    if (id && has({ menuContext, key: 'canCreate' })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && has({ menuContext, key: 'canView' })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && isActive && has({ menuContext, key: 'canEdit' })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: 'canActive' })) {
        col2.push(createButtonActive({ onClick: () => onClickActive({ record: { id }, callActive, setIsLoading: setIsLoadingApp, onClickEdit: () => onClickEdit({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canArchive' })) {
        col2.push(createButtonArchive({ onClick: () => onClickArchive({ record: { id }, callArchive, setIsLoading: setIsLoadingApp, onClickView: () => onClickView({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canDelete' })) {
        col2.push(createButtonDelete({ onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }), classStyle: 'm-1' }))
    }
    return <Row type="flex">
        <Col>{col1}</Col>
        <Col class="ml-auto">{col2}</Col>
    </Row>
}

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => <CIFrame src={`${process.env.VUE_APP_PUBLIC_PATH}/app/master/attachments?isLayout=false&canSelect=true&cacheQuery=true`} onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}></CIFrame>
    })
    return options
}

export const openModalForm = ({ props, items }) => {
    const refs = reactive({ form: null })
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            okButtonProps: { disabled: props.viewOnly },
            header: i18n.global.t('model.attachment.attachments'),
            onOk: () => {
                refs.form.callOnClickModalOk()
                    .then((record) => {
                        if (items) {
                            items.push(record)
                        } else {
                            lodash.merge(props.form, record)
                        }
                        options.bind.onCancel()
                    })
            }
        }),
        render: () => <AttachmentForm ref={(e) => { if (e) refs.form = e }} {...props}></AttachmentForm>
    })
    return options
}

export const initData = ({ props, data }) => {
    const { menuContext, viewOnly } = props
    data.columns = createColumns({ props, data })
    data.page = createPage({ data: props.parent.attachments })
    data.tableOptions = { search: { activeKey: null } }
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => viewOnly ? false : has({ menuContext, key: 'canDelete' }),
            onClick: ({ rowSelection }) => onClickDeleteAllClient({ rowSelection, items: props.parent.attachments }),
        }),
        createManageExportCsv({ menuContext }),
    ]
    data.manageExtras = []
    data.rowActions = []
    if (!viewOnly) {
        data.manageExtras.push({
            render: () => ({
                ...createButtonCreate({
                    onClick: () => openModalForm({ props: { menuContext, viewOnly, setAppPageHeader: false, form: {}, group: props.group }, items: props.parent.attachments }),
                    options: { text: i18n.global.t('button.add') }
                }),
            }),
            visible: () => has({ menuContext, key: 'canCreate' }),
        })

        data.rowActions.push(createRowActionEdit({ menuContext, onClick: ({ record }) => openModalForm({ props: { menuContext, viewOnly, setAppPageHeader: false, form: record, group: props.group } }) }))
    } else {
        data.rowActions.push(createRowActionView({ menuContext, onClick: ({ record }) => openModalForm({ props: { menuContext, viewOnly, setAppPageHeader: false, form: record, group: props.group } }) }))
    }

    fetchData({ props, data }).then(page => {
        if (!props.parent.attachments.length) {
            props.parent.attachments.push(...page.data)
        }
    })
}

export const fetchData = ({ props }) => {
    const { fetchCriteria } = props
    if (!fetchCriteria) {
        return Promise.resolve({ data: [] })
    }
    setIsLoading(true)
    return callSearchByModelAndRefId({ criteria: fetchCriteria }).finally(() => setIsLoading(false))
}

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { select: ['*'] } }),
    tableOptions: { search: { activeKey: 'Search' } }
})
